import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Calendar } from './Calendar';
import { PilotoAutomatico } from './PilotoAutomatico';
import { Loader2 } from 'lucide-react';

export const CalendarRouter = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shouldShowCalendar, setShouldShowCalendar] = useState(false);

  useEffect(() => {
    checkConfigSettings();
  }, []);

  // Re-check when other parts of the app notify us
  useEffect(() => {
    const handler = () => { void checkConfigSettings(); };
    window.addEventListener('calendar-recheck' as any, handler as EventListener);
    window.addEventListener('media-initial-gen-started' as any, handler as EventListener);
    return () => {
      window.removeEventListener('calendar-recheck' as any, handler as EventListener);
      window.removeEventListener('media-initial-gen-started' as any, handler as EventListener);
    };
  }, []);

  const checkConfigSettings = async () => {
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!(profile as any)?.id_company) throw new Error('No company associated with user');

      const { data, error } = await supabase
        .schema((profile as any).id_company)
        .from('config')
        .select('media_agent_analysis, media_agent_on, last_auto_gen_date')
        .eq('id', 0)
        .maybeSingle();

      if (error) throw error;
      
      // Determine if we have analysis configured and that initial generation has run
      const hasAnalysis = data?.media_agent_analysis !== null && data?.media_agent_analysis !== undefined;
      const hasInitialGeneration = !!data?.last_auto_gen_date;
      const hasAgentOn = data?.media_agent_on === true;
      // One-time session override: if initial gen was just started, allow calendar
      let sessionOverride = false;
      try {
        const flag = window.sessionStorage.getItem('media_initial_generation_started');
        if (flag === 'true') {
          sessionOverride = true;
          window.sessionStorage.removeItem('media_initial_generation_started');
        }
      } catch {}
      console.log('[CalendarRouter] config:', {
        hasAnalysis,
        last_auto_gen_date: data?.last_auto_gen_date,
        hasInitialGeneration,
        hasAgentOn,
        sessionOverride
      });

      // Check Instagram connection status using company schema instagram_data
      let instagramConnected = false;
      if ((profile as any).id_company) {
        try {
          const { data: instagramData } = await supabase
            .schema((profile as any).id_company)
            .from('instagram_data')
            .select('instagram_username')
            .eq('id', 0)
            .maybeSingle();
          if (instagramData?.instagram_username) {
            instagramConnected = true;
          }
        } catch (_e) {
          // Treat as not connected
        }
      }

      console.log('[CalendarRouter] instagramConnected:', instagramConnected);
      // Only show Calendar when analysis exists, Instagram is connected, and one of:
      // - initial generation has happened
      // - agent is ON
      // - we just started generation in this session
      setShouldShowCalendar(
        hasAnalysis && instagramConnected && (hasInitialGeneration || hasAgentOn || sessionOverride)
      );
    } catch (err) {
      console.error('Error checking config settings:', err);
      setError(err instanceof Error ? err.message : 'Error checking configuration');
      // Default to BrandDescription if there's an error
      setShouldShowCalendar(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    console.error('Error in CalendarRouter:', error);
    // If there's an error, default to PilotoAutomatico
    return <PilotoAutomatico />;
  }

  // Render the appropriate component based on the config settings
  return shouldShowCalendar ? <Calendar /> : <PilotoAutomatico />;
};

export default CalendarRouter;
